// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { WorkstationSetting, WorkstationSettingsView } from '@api/types';

export const SettingId = {
  GRACE_PERIOD: 'grace-period-secs',
  FADE_TIME: 'workstation-lock-fade-time',
  INACTIVITY_TIMEOUT: 'workstation-lock-inactivity-timeout',
  SHARED_LOCK: 'shared_ws_lock_behavior',
  PRIVATE_LOCK: 'private_ws_lock_behavior',
} as const;
export type SettingId = (typeof SettingId)[keyof typeof SettingId];

const SETTING_IDS: readonly string[] = Object.values(SettingId);
export const isSettingId = (id: string): id is SettingId => SETTING_IDS.includes(id);

export const SectionId = {
  AUTHENTICATION: 'authentication',
  DESKTOP: 'desktop',
  EPIC: 'epic',
} as const;
export type SectionId = (typeof SectionId)[keyof typeof SectionId];

const SECTION_IDS: readonly string[] = Object.values(SectionId);
export const isSectionId = (id: string): id is SectionId => SECTION_IDS.includes(id);

export const ValueSource = { DEFAULT: 'DEFAULT', GROUP_OVERRIDE: 'GROUP_OVERRIDE' } as const;

// The line under a field. `none` covers read-only settings, the Default settings view and
// a valueSource the UI does not know.
export type Status =
  | { kind: 'none' }
  | { kind: 'default' }
  | { kind: 'override'; canReset: boolean };

export type Control =
  // `presets` are the listed `values`, in seconds, which are allowed even outside `range`.
  | {
      kind: 'duration';
      seconds: number;
      range?: { min: number; max: number };
      presets: readonly number[];
    }
  | { kind: 'hours-minutes'; seconds: number; maxHours: number; max: number }
  | { kind: 'choice'; value: string; values: readonly string[] }
  // An unknown setting, or a known one whose options break what its control needs.
  | {
      kind: 'generic';
      value: string;
      values: readonly string[];
      range?: { min: number; max: number };
    };

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

const DECIMAL = /^\d+$/;

export const parseDecimal = (value: string): number | null =>
  DECIMAL.test(value) && Number.isSafeInteger(Number(value)) ? Number(value) : null;

const wholeMinutes = (seconds: number): number | null =>
  seconds % SECONDS_PER_MINUTE === 0 ? seconds / SECONDS_PER_MINUTE : null;

export const toHoursMinutesSeconds = (total: number) => ({
  hours: Math.floor(total / SECONDS_PER_HOUR),
  minutes: Math.floor((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE),
  seconds: total % SECONDS_PER_MINUTE,
});

export const fromHoursMinutesSeconds = (hours: number, minutes: number, seconds: number): number =>
  hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;

// The contract's rule for update: one of `values`, or a custom value within `customRange`.
export const acceptsValue = ({ options }: WorkstationSetting, value: string): boolean => {
  if (options.values.includes(value)) {
    return true;
  }
  const seconds = parseDecimal(value);
  const range = options.customRange;
  return (
    options.allowCustom &&
    range !== undefined &&
    seconds !== null &&
    seconds >= range.min &&
    seconds <= range.max
  );
};

// A Desktop duration keeps to the BFF's `customRange` (user, 2026-10-16), or to 24 hours in all
// when the BFF sends none.
export const MAX_DURATION_HOURS = 24;

// Minutes or seconds: 0 to 59.
export const acceptsSexagesimal = (value: number): boolean => value < SECONDS_PER_MINUTE;

// One of the presets, or within `range`, in seconds and inclusive, or within 24 hours without
// one: the contract's update rule (`acceptsValue`) in seconds.
export const acceptsTotal = (
  total: number,
  range?: { min: number; max: number },
  presets: readonly number[] = [],
): boolean =>
  presets.includes(total) ||
  (range
    ? total >= range.min && total <= range.max
    : total <= MAX_DURATION_HOURS * SECONDS_PER_HOUR);

export const acceptsDuration = (
  hours: number,
  minutes: number,
  seconds: number,
  range?: { min: number; max: number },
  presets: readonly number[] = [],
): boolean =>
  acceptsSexagesimal(minutes) &&
  acceptsSexagesimal(seconds) &&
  acceptsTotal(fromHoursMinutesSeconds(hours, minutes, seconds), range, presets);

// The current value is always listed, so a value outside `values` still shows as saved.
const valuesWith = (values: readonly string[], value: string): readonly string[] =>
  values.includes(value) ? values : [value, ...values];

const genericOf = ({ effectiveValue, options }: WorkstationSetting): Control => ({
  kind: 'generic',
  value: effectiveValue,
  values: valuesWith(options.values, effectiveValue),
  range: options.allowCustom ? options.customRange : undefined,
});

const durationOf = (setting: WorkstationSetting): Control => {
  const seconds = parseDecimal(setting.effectiveValue);
  if (!setting.options.allowCustom || seconds === null) {
    return genericOf(setting);
  }
  const range = setting.options.customRange;
  const presets = setting.options.values
    .map(parseDecimal)
    .filter((preset): preset is number => preset !== null);
  return range
    ? { kind: 'duration', seconds, range, presets }
    : { kind: 'duration', seconds, presets };
};

const hoursMinutesOf = (setting: WorkstationSetting): Control => {
  const range = setting.options.allowCustom ? setting.options.customRange : undefined;
  const seconds = parseDecimal(setting.effectiveValue);
  if (range === undefined || seconds === null || wholeMinutes(seconds) === null) {
    return genericOf(setting);
  }
  // A saved value above the range still gets its own hour option.
  const maxHours = Math.floor(Math.max(range.max, seconds) / SECONDS_PER_HOUR);
  return { kind: 'hours-minutes', seconds, maxHours, max: range.max };
};

const CONTROL_OF: Record<SettingId, (setting: WorkstationSetting) => Control> = {
  [SettingId.GRACE_PERIOD]: hoursMinutesOf,
  [SettingId.FADE_TIME]: durationOf,
  [SettingId.INACTIVITY_TIMEOUT]: durationOf,
  [SettingId.SHARED_LOCK]: ({ effectiveValue, options }) => ({
    kind: 'choice',
    value: effectiveValue,
    values: valuesWith(options.values, effectiveValue),
  }),
  [SettingId.PRIVATE_LOCK]: ({ effectiveValue, options }) => ({
    kind: 'choice',
    value: effectiveValue,
    values: valuesWith(options.values, effectiveValue),
  }),
};

export const controlOf = (setting: WorkstationSetting): Control =>
  isSettingId(setting.id) ? CONTROL_OF[setting.id](setting) : genericOf(setting);

// Fade to lock duration must not be longer than Time to inactivity lock (user, 2026-10-19).
export type DurationLimit = { bound: 'max' | 'min'; seconds: number; setting: WorkstationSetting };

const DURATION_LIMITS: Partial<
  Record<SettingId, { bound: DurationLimit['bound']; of: SettingId }>
> = {
  [SettingId.FADE_TIME]: { bound: 'max', of: SettingId.INACTIVITY_TIMEOUT },
  [SettingId.INACTIVITY_TIMEOUT]: { bound: 'min', of: SettingId.FADE_TIME },
};

// The other duration in the same view, at the strictest of its saved value and the values its
// pending writes will save, so the pair holds whichever writes succeed. None if it is missing or
// malformed.
export const durationLimitOf = (
  settings: readonly WorkstationSetting[],
  setting: WorkstationSetting,
  pending: ReadonlyMap<string, readonly string[]> = new Map(),
): DurationLimit | undefined => {
  const limit = isSettingId(setting.id) ? DURATION_LIMITS[setting.id] : undefined;
  const other = limit && settings.find(candidate => candidate.id === limit.of);
  const control = other && controlOf(other);
  if (!limit || !other || control?.kind !== 'duration') {
    return undefined;
  }
  const values = [
    control.seconds,
    ...(pending.get(other.id) ?? [])
      .map(parseDecimal)
      .filter((value): value is number => value !== null),
  ];
  const seconds = limit.bound === 'max' ? Math.min(...values) : Math.max(...values);
  return { bound: limit.bound, seconds, setting: other };
};

export type LimitError = 'longer-than-error' | 'shorter-than-error';

export const limitErrorOf = (
  limit: Pick<DurationLimit, 'bound' | 'seconds'> | undefined,
  seconds: number,
): LimitError | null => {
  if (limit?.bound === 'max' && seconds > limit.seconds) {
    return 'longer-than-error';
  }
  if (limit?.bound === 'min' && seconds < limit.seconds) {
    return 'shorter-than-error';
  }
  return null;
};

// A known setting the UI cannot render as designed. Logged, since it is a BFF defect.
export const isMalformed = (setting: WorkstationSetting): boolean =>
  isSettingId(setting.id) && controlOf(setting).kind === 'generic';

export const statusOf = (groupId: string | undefined, setting: WorkstationSetting): Status => {
  if (groupId === undefined) {
    return { kind: 'none' };
  }
  if (setting.valueSource === ValueSource.GROUP_OVERRIDE) {
    return { kind: 'override', canReset: setting.actions.reset };
  }
  if (setting.valueSource === ValueSource.DEFAULT && setting.actions.edit) {
    return { kind: 'default' };
  }
  return { kind: 'none' };
};

// Where each known setting is shown, whatever section the BFF puts it in, so a known setting's
// heading, description and place on the page never come from the BFF.
const SECTION_OF_SETTING: Record<SettingId, SectionId> = {
  [SettingId.GRACE_PERIOD]: SectionId.AUTHENTICATION,
  [SettingId.FADE_TIME]: SectionId.DESKTOP,
  [SettingId.INACTIVITY_TIMEOUT]: SectionId.DESKTOP,
  [SettingId.SHARED_LOCK]: SectionId.EPIC,
  [SettingId.PRIVATE_LOCK]: SectionId.EPIC,
};

export type PageSection = { id: string; settings: WorkstationSetting[] };

// The page's sections in a fixed order: authentication (Proximity cards follow it), desktop, epic,
// then any section the UI does not know, in BFF order. Known settings keep the SettingId order
// within their section, so the layout never follows the BFF's order. A known setting goes to its
// own section; an unknown one stays in the BFF's section.
export const pageSectionsOf = ({ sections }: WorkstationSettingsView): PageSection[] => {
  const byId = new Map<string, WorkstationSetting[]>(SECTION_IDS.map(id => [id, []]));
  for (const section of sections) {
    for (const setting of section.settings) {
      const id = isSettingId(setting.id) ? SECTION_OF_SETTING[setting.id] : section.id;
      byId.set(id, [...(byId.get(id) ?? []), setting]);
    }
  }
  const rank = (setting: WorkstationSetting) => SETTING_IDS.indexOf(setting.id);
  return [...byId]
    .filter(([, settings]) => settings.length > 0)
    .map(([id, settings]) => ({
      id,
      settings: isSectionId(id) ? [...settings].sort((a, b) => rank(a) - rank(b)) : settings,
    }));
};

export const overrideCount = ({ sections }: WorkstationSettingsView): number =>
  sections
    .flatMap(section => section.settings)
    .filter(setting => setting.valueSource === ValueSource.GROUP_OVERRIDE).length;

export const withSetting = (
  view: WorkstationSettingsView,
  setting: WorkstationSetting,
): WorkstationSettingsView => ({
  ...view,
  sections: view.sections.map(section => ({
    ...section,
    settings: section.settings.map(current => (current.id === setting.id ? setting : current)),
  })),
});

export const toHoursMinutes = (seconds: number) => ({
  hours: Math.floor(seconds / SECONDS_PER_HOUR),
  minutes: Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE),
});

export const fromHoursMinutes = (hours: number, minutes: number): number =>
  hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE;
